import {
  CLAIM_STATUS,
  claimStatusEnumValues,
  ClaimStatusValue,
} from '@billing/validation/baseClaim';
import {
  CLAIM_TARIFF_STATUS,
  claimTariffStatusEnumValues,
  ClaimTariffStatusValue,
  UNRECONCILED_CLAIM_TARIFF_STATUSES,
} from '@billing/validation/tariff';
import { Claim } from 'interfaces';

const claimStatusSet = new Set<string>(claimStatusEnumValues);
const claimTariffStatusSet = new Set<string>(claimTariffStatusEnumValues);

export const isClaimStatusValue = (value: unknown): value is ClaimStatusValue =>
  typeof value === 'string' && claimStatusSet.has(value);

export const isClaimTariffStatusValue = (
  value: unknown
): value is ClaimTariffStatusValue =>
  typeof value === 'string' && claimTariffStatusSet.has(value);

export const normalizeLegacyTariffStatus = (
  rawStatus: unknown
): ClaimTariffStatusValue | undefined => {
  // TODO(claim-status-migration-cleanup): Remove this legacy remap after
  // release_2.15.0 is fully deployed everywhere and no clients/tests emit
  // claim-level statuses (Error/Unreconciled) on tariff rows.
  if (typeof rawStatus !== 'string') return undefined;
  if (rawStatus === 'Error') return CLAIM_TARIFF_STATUS.REJECTED;
  if (rawStatus === 'Unreconciled') return CLAIM_TARIFF_STATUS.UNDERPAID;
  return isClaimTariffStatusValue(rawStatus) ? rawStatus : undefined;
};

export const getClaimTariffStatuses = (
  claim: Claim
): ClaimTariffStatusValue[] => {
  const tariffs = Array.isArray(claim?.tariffs) ? claim.tariffs : [];
  return tariffs
    .map((tariff) => normalizeLegacyTariffStatus(tariff?.status))
    .filter((status): status is ClaimTariffStatusValue => Boolean(status));
};

export const claimHasTariffStatus = (
  claim: Claim,
  status: ClaimTariffStatusValue
): boolean => getClaimTariffStatuses(claim).includes(status);

export const claimHasAnyTariffStatus = (
  claim: Claim,
  statuses: readonly ClaimTariffStatusValue[]
): boolean => {
  const set = new Set(getClaimTariffStatuses(claim));
  return statuses.some((status) => set.has(status));
};

export const isUnreconciledTariffStatus = (
  status: string | null | undefined
): status is
  | typeof CLAIM_TARIFF_STATUS.UNDERPAID
  | typeof CLAIM_TARIFF_STATUS.OVERPAID =>
  status === CLAIM_TARIFF_STATUS.UNDERPAID ||
  status === CLAIM_TARIFF_STATUS.OVERPAID;

export const isReconcileEligibleTariffStatus = (
  status: string | null | undefined
): status is
  | typeof CLAIM_TARIFF_STATUS.REJECTED
  | typeof CLAIM_TARIFF_STATUS.UNDERPAID
  | typeof CLAIM_TARIFF_STATUS.OVERPAID =>
  status === CLAIM_TARIFF_STATUS.REJECTED ||
  isUnreconciledTariffStatus(status);

export const isResubmittableTariffStatus = (
  status: string | null | undefined
): status is
  | typeof CLAIM_TARIFF_STATUS.REJECTED
  | typeof CLAIM_TARIFF_STATUS.UNDERPAID
  | typeof CLAIM_TARIFF_STATUS.OVERPAID =>
  status === CLAIM_TARIFF_STATUS.REJECTED ||
  status === CLAIM_TARIFF_STATUS.UNDERPAID ||
  status === CLAIM_TARIFF_STATUS.OVERPAID;

export const isWriteOffEligibleTariffStatus = (
  status: string | null | undefined
): status is
  | typeof CLAIM_TARIFF_STATUS.REJECTED
  | typeof CLAIM_TARIFF_STATUS.PENDING_WRITE_OFF =>
  status === CLAIM_TARIFF_STATUS.REJECTED ||
  status === CLAIM_TARIFF_STATUS.PENDING_WRITE_OFF;

export const areTariffStatusesEligible = (
  statuses: Array<ClaimTariffStatusValue | undefined>,
  eligibility: (status: ClaimTariffStatusValue) => boolean
): boolean =>
  statuses.length > 0 &&
  statuses.every((status) => Boolean(status) && eligibility(status));

export const claimHasUnreconciledTariffStatus = (claim: Claim): boolean =>
  claimHasAnyTariffStatus(claim, UNRECONCILED_CLAIM_TARIFF_STATUSES);

export const claimHasOnlyTariffStatuses = (
  claim: Claim,
  allowed: ClaimTariffStatusValue[]
): boolean => {
  const statuses = getClaimTariffStatuses(claim);
  if (!statuses.length) return false;
  const allowedSet = new Set(allowed);
  return statuses.every((status) => allowedSet.has(status));
};

export const isCancelledClaim = (claim: Claim): boolean =>
  claim?.status?.trim?.() === CLAIM_STATUS.CANCELLED;
